import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, ScanLine } from "lucide-react";

/**
 * Camera-driven QR scanner.
 * Calls onScan(decodedText) once on first decode then auto-stops.
 */
export default function CameraScanner({ onScan, onClose }) {
  const ref = useRef(null);
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const id = "cam-scan-region";

    (async () => {
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!cams || cams.length === 0) {
          setError("No camera found on this device.");
          setStarting(false);
          return;
        }
        const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
        const html5Qr = new Html5Qrcode(id, { verbose: false });
        scannerRef.current = html5Qr;
        await html5Qr.start(
          back.id,
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (!decodedText) return;
            // Stop and bubble up
            html5Qr.stop().catch(() => {});
            onScan?.(decodedText);
          },
          () => {}
        );
        if (!cancelled) setStarting(false);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Cannot access camera");
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      if (inst) {
        try {
          inst.stop().catch(() => {}).finally(() => {
            try { inst.clear(); } catch (e) { /* ignore */ }
          });
        } catch (e) { /* ignore */ }
      }
    };
  }, [onScan]);

  return (
    <div data-testid="camera-scanner" className="fixed inset-0 z-[100] bg-[#0B0F0C]/95 backdrop-blur-sm flex items-center justify-center p-5">
      <div className="card-x card-green bg-[#FAF6E8] p-5 max-w-md w-full">
        <div className="flex items-center gap-2 mb-3">
          <ScanLine size={16} strokeWidth={2.4} />
          <span className="font-display text-2xl font-extrabold">Point at QR</span>
          <button data-testid="camera-close" onClick={onClose} className="ml-auto rounded-full border-2 border-[#0B0F0C] w-9 h-9 flex items-center justify-center bg-[#FAF6E8] hover:bg-[#FF1A8C] hover:text-white transition-all">
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>

        <div id="cam-scan-region" ref={ref} className="rounded-2xl overflow-hidden border-2 border-[#0B0F0C] bg-black aspect-square" />

        {starting && (
          <div className="text-xs font-mono-x font-bold uppercase tracking-widest mt-3 text-center opacity-70">
            <Camera size={12} strokeWidth={2.4} className="inline mr-1" /> requesting camera…
          </div>
        )}
        {error && (
          <div className="mt-3 text-sm font-mono-x font-bold text-[#FF1A8C]">{error}</div>
        )}
        {!error && !starting && (
          <div className="text-xs font-mono-x font-bold uppercase tracking-widest mt-3 text-center opacity-70">
            scanning… hold steady
          </div>
        )}
      </div>
    </div>
  );
}
